import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Info, Star, Trophy, Users } from 'lucide-react';
import { Game } from '@/services/gamesService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';

interface ScorekeeperApplicationModalProps {
  game: Game | null;
  isOpen: boolean;
  onClose: () => void;
  onApplicationSubmitted: () => void;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  display_order: number;
}

interface TeamInfo {
  team_id: number;
  team_name: string;
  team_logo?: string;
}

export const ScorekeeperApplicationModal: React.FC<ScorekeeperApplicationModalProps> = ({
  game,
  isOpen,
  onClose,
  onApplicationSubmitted
}) => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasReadFAQs, setHasReadFAQs] = useState(false);
  const [acknowledgeResponsibilities, setAcknowledgeResponsibilities] = useState(false);
  const [existingApplication, setExistingApplication] = useState<any>(null);
  const [homeTeam, setHomeTeam] = useState<TeamInfo | null>(null);
  const [awayTeam, setAwayTeam] = useState<TeamInfo | null>(null);

  // Check if game is eligible for scorekeeper applications
  const isGameEligible = () => {
    if (!game) return false;
    
    // Check if this is a venue game
    const venueGame = game as any;
    if (venueGame.status) {
      // For venue games, both game_status should be 'confirmed' and status should be 'approved'
      return venueGame.game_status === 'confirmed' && venueGame.status === 'approved';
    }
    
    // For regular games, just check game_status
    return game.game_status === 'confirmed';
  };

  useEffect(() => {
    if (isOpen && game) {
      loadFAQs();
      checkExistingApplication();
      loadTeamInformation();
    }
  }, [isOpen, game, user]);

  const loadFAQs = async () => {
    try {
      const { data, error } = await supabase
        .from('scorekeeper_faqs')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error loading FAQs:', error);
    }
  };

  const loadTeamInformation = async () => {
    if (!game) return;

    try {
      const venueGame = game as any;
      
      // Load home team info
      if (venueGame.home_team_id) {
        const { data: homeTeamData, error: homeError } = await supabase
          .from('teams')
          .select('team_id, team_name, team_logo')
          .eq('team_id', venueGame.home_team_id)
          .single();

        if (homeError) throw homeError;
        setHomeTeam(homeTeamData);
      }

      // Load away team info
      if (venueGame.away_team_id) {
        const { data: awayTeamData, error: awayError } = await supabase
          .from('teams')
          .select('team_id, team_name, team_logo')
          .eq('team_id', venueGame.away_team_id)
          .single();

        if (awayError) throw awayError;
        setAwayTeam(awayTeamData);
      }
    } catch (error) {
      console.error('Error loading team information:', error);
    }
  };

  const checkExistingApplication = async () => {
    if (!user || !game) return;

    try {
      const { data, error } = await supabase
        .from('scorekeeper_applications')
        .select('*')
        .eq('game_id', game.id)
        .eq('scorekeeper_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setExistingApplication(data);
    } catch (error) {
      console.error('Error checking existing application:', error);
    }
  };

  const handleSubmitApplication = async () => {
    if (!user || !game) {
      toast.error('Please log in to apply');
      return;
    }

    if (!hasReadFAQs || !acknowledgeResponsibilities) {
      toast.error('Please read the FAQs and acknowledge your responsibilities');
      return;
    }

    setLoading(true);
    try {
      // Get the next backup position
      const { data: existingApps, error: fetchError } = await supabase
        .from('scorekeeper_applications')
        .select('backup_position')
        .eq('game_id', game.id)
        .order('backup_position', { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error('Error getting existing applications:', fetchError);
        toast.error('Failed to check existing applications');
        return;
      }

      const nextPosition = existingApps && existingApps.length > 0 
        ? Math.min(existingApps[0].backup_position + 1, 4)
        : 1;

      if (nextPosition > 4) {
        toast.error('All scorekeeper positions are filled');
        return;
      }

      const { error: insertError } = await supabase
        .from('scorekeeper_applications')
        .insert({
          game_id: game.id,
          scorekeeper_id: user.id,
          backup_position: nextPosition,
          status: 'pending'
        });

      if (insertError) throw insertError;

      toast.success(`Application submitted successfully! You are ${nextPosition === 1 ? 'primary' : `backup #${nextPosition}`} scorekeeper.`);
      onApplicationSubmitted();
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (!game) return null;

  const responsibilityFAQs = faqs.filter(f => f.category === 'responsibilities');
  const statusFAQs = faqs.filter(f => f.category === 'status_definitions');
  const generalFAQs = faqs.filter(f => f.category === 'general');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2 text-white">
            <Star className="h-6 w-6 text-sports-green" />
            Apply as Scorekeeper
          </DialogTitle>
          
          {/* Team Matchup Display */}
          <div className="mt-4 p-4 bg-gray-800 border border-gray-700 rounded-lg">
            <div className="flex items-center justify-between">
              {/* Home Team */}
              <div className="flex items-center gap-3 flex-1">
                {homeTeam?.team_logo ? (
                  <img 
                    src={homeTeam.team_logo} 
                    alt={`${homeTeam.team_name} logo`}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-white">
                    {homeTeam?.team_name || 'Home Team TBD'}
                  </p>
                  <p className="text-sm text-gray-400">Home</p>
                </div>
              </div>

              {/* VS Indicator */}
              <div className="flex-shrink-0 mx-4">
                <div className="bg-sports-green/20 border border-sports-green/30 rounded-full p-2">
                  <Trophy className="h-5 w-5 text-sports-green" />
                </div>
              </div>

              {/* Away Team */}
              <div className="flex items-center gap-3 flex-1 justify-end text-right">
                <div>
                  <p className="font-semibold text-white">
                    {awayTeam?.team_name || 'Away Team TBD'}
                  </p>
                  <p className="text-sm text-gray-400">Away</p>
                </div>
                {awayTeam?.team_logo ? (
                  <img 
                    src={awayTeam.team_logo} 
                    alt={`${awayTeam.team_name} logo`}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            
            {/* Game Details */}
            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Date & Time</p>
                  <p className="text-white">{(game as any).game_date} at {(game as any).game_time}</p>
                </div>
                <div>
                  <p className="text-gray-400">Points Offered</p>
                  <p className="text-sports-green font-bold">{(game as any).scorekeeper_points || 50} pts</p>
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {!isGameEligible() ? (
          <Card className="bg-destructive/10 border-destructive/20">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-destructive mb-2" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-destructive mb-2">
                    Scorekeeper Applications Not Available
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Scorekeeper applications are only available for games that are confirmed and approved.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span>Game Status:</span>
                      <Badge variant={game.game_status === 'confirmed' ? 'default' : 'destructive'}>
                        {game.game_status}
                      </Badge>
                    </div>
                    {(game as any).status && (
                      <div className="flex justify-between items-center">
                        <span>Approval Status:</span>
                        <Badge variant={(game as any).status === 'approved' ? 'default' : 'destructive'}>
                          {(game as any).status}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Requirements for Scorekeeper Applications:</strong><br/>
                      • Game must be in "confirmed" status<br/>
                      • Game must be "approved" by the venue (if applicable)<br/>
                      • Game must not be cancelled or rejected
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : existingApplication ? (
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center">
                  <Badge variant={existingApplication.status === 'approved' ? 'default' : 'outline'} className="text-lg px-4 py-2">
                    {existingApplication.status === 'approved' ? 'Application Approved' : 
                     existingApplication.status === 'rejected' ? 'Application Rejected' : 'Application Pending'}
                  </Badge>
                </div>
                <p className="text-muted-foreground">
                  You have already applied as {existingApplication.backup_position === 1 ? 'primary' : `backup #${existingApplication.backup_position}`} scorekeeper for this game.
                </p>
                {existingApplication.status === 'rejected' && existingApplication.rejection_reason && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <p className="text-sm text-destructive">
                      Reason: {existingApplication.rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Scorekeeper Responsibilities */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Scorekeeper Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-48">
                  <div className="space-y-4">
                    {responsibilityFAQs.map((faq) => (
                      <div key={faq.id} className="space-y-2">
                        <h4 className="font-medium text-foreground">{faq.question}</h4>
                        <p className="text-sm text-muted-foreground">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Status Definitions */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Performance Status Definitions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {statusFAQs.map((faq) => (
                    <div key={faq.id} className="space-y-2 p-3 bg-muted/50 rounded-lg">
                      <h4 className="font-medium text-foreground">{faq.question}</h4>
                      <p className="text-sm text-muted-foreground">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* General Information */}
            {generalFAQs.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {generalFAQs.map((faq) => (
                      <div key={faq.id} className="space-y-2">
                        <h4 className="font-medium text-foreground">{faq.question}</h4>
                        <p className="text-sm text-muted-foreground">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Points Information Card */}
            <Card className="bg-gray-800 border-sports-green/50">
              <CardHeader>
                <CardTitle className="text-white">Scorekeeper Points System</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="bg-sports-green/20 border border-sports-green/30 p-3 rounded-lg">
                    <p className="font-semibold text-white mb-1">This Game Offers:</p>
                    <p className="text-2xl font-bold text-sports-green">{(game as any).scorekeeper_points || 50} Player Points</p>
                  </div>
                  <div className="space-y-2 text-gray-200">
                    <p>• <strong className="text-white">Points are only awarded when the game is completed and properly scored</strong></p>
                    <p>• No points will be received if the game is not scored until completion</p>
                    <p>• Point amounts are set by the Coach/Team Owner when submitting the game request</p>
                    <p>• Points are added to your player account for rewards and leaderboard ranking</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Acknowledgment */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="read-faqs" 
                      checked={hasReadFAQs}
                      onCheckedChange={(checked) => setHasReadFAQs(checked as boolean)}
                      className="mt-1"
                    />
                    <label htmlFor="read-faqs" className="text-sm font-medium text-white leading-relaxed cursor-pointer">
                      I have read and understand all the scorekeeper information above
                    </label>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="acknowledge-responsibilities" 
                      checked={acknowledgeResponsibilities}
                      onCheckedChange={(checked) => setAcknowledgeResponsibilities(checked as boolean)}
                      className="mt-1"
                    />
                    <label htmlFor="acknowledge-responsibilities" className="text-sm font-medium text-white leading-relaxed cursor-pointer">
                      I acknowledge my responsibilities as a scorekeeper and understand that my performance will be evaluated
                    </label>
                  </div>

                  <div className="bg-yellow-600/20 border border-yellow-500/50 rounded-lg p-4">
                    <p className="text-sm text-yellow-200 font-medium">
                      Important: By applying, you commit to being available for the entire game duration. 
                      Failure to show up or complete your duties will negatively impact your scorekeeper rating.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitApplication}
                disabled={!hasReadFAQs || !acknowledgeResponsibilities || loading}
                className="min-w-32"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
